'use client';

import { useEffect, useMemo, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { DiscoverDialogClose } from '@/components/help-center/DiscoverDialogClose';
import { PriceActivityEmpty, PriceActivityError } from '@/components/help-center/fairtrade/price-activity/PriceActivityEmpty';
import { PriceActivityFilters } from '@/components/help-center/fairtrade/price-activity/PriceActivityFilters';
import { PriceActivityHeader } from '@/components/help-center/fairtrade/price-activity/PriceActivityHeader';
import { PriceActivityList } from '@/components/help-center/fairtrade/price-activity/PriceActivityList';
import { PriceActivitySkeleton } from '@/components/help-center/fairtrade/price-activity/PriceActivitySkeleton';
import { PriceActivitySummary } from '@/components/help-center/fairtrade/price-activity/PriceActivitySummary';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { PRICE_ACTIVITY_MODAL_PAGE_SIZE } from '@/lib/price-activity-config';
import {
  filterPriceActivityChanges,
  summarizeCategoryChanges,
} from '@/lib/price-activity';
import type {
  PriceActivityChange,
  PriceActivityDirectionFilter,
  PriceActivityMetricFilter,
} from '@/lib/price-activity-types';
import { cn } from '@/lib/utils';
import { listPriceActivityByCategory } from '@/services/priceActivity.service';

type PriceActivityModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categoryId: string;
  categoryLabel: string;
  sampleIcon: string;
  onViewAllPowers: () => void;
};

export function PriceActivityModal({
  open,
  onOpenChange,
  categoryId,
  categoryLabel,
  sampleIcon,
  onViewAllPowers,
}: PriceActivityModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [changes, setChanges] = useState<PriceActivityChange[]>([]);
  const [direction, setDirection] = useState<PriceActivityDirectionFilter>('all');
  const [metric, setMetric] = useState<PriceActivityMetricFilter>('all');
  const [visibleCount, setVisibleCount] = useState(PRICE_ACTIVITY_MODAL_PAGE_SIZE);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const list = await listPriceActivityByCategory(categoryId);
      setChanges(list);
    } catch {
      setError(true);
      setChanges([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setDirection('all');
    setMetric('all');
    setVisibleCount(PRICE_ACTIVITY_MODAL_PAGE_SIZE);
    void load();
  }, [open, categoryId]);

  const summary = useMemo(
    () => summarizeCategoryChanges(categoryId, changes),
    [categoryId, changes]
  );

  const filtered = useMemo(
    () => filterPriceActivityChanges(changes, { direction, metric }),
    [changes, direction, metric]
  );

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          'flex max-h-[min(94vh,860px)] w-[min(96vw,36rem)] max-w-xl flex-col gap-0 overflow-hidden p-0',
          'border border-solid border-blue-900/60 bg-[#0a1048] text-sky-50',
          'shadow-2xl shadow-black/50 ring-1 ring-sky-400/20',
          'sm:rounded-2xl',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95'
        )}
      >
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#000b6a]/80 via-[#030023]/40 to-transparent"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-sky-500/[0.07] via-transparent to-indigo-600/10"
          aria-hidden
        />

        <DiscoverDialogClose className="right-4 top-4" />

        <div className="relative shrink-0 border-b border-white/[0.08] px-5 py-5 sm:px-6">
          <DialogTitle className="sr-only">
            Price Activity — {categoryLabel}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Últimas alterações nos valores dos powers de {categoryLabel}.
          </DialogDescription>
          <PriceActivityHeader
            categoryId={categoryId}
            categoryLabel={categoryLabel}
            sampleIcon={sampleIcon}
            summary={summary}
          />
        </div>

        <div className="relative min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
          {loading ? (
            <PriceActivitySkeleton />
          ) : error ? (
            <PriceActivityError onRetry={() => void load()} />
          ) : changes.length === 0 ? (
            <PriceActivityEmpty categoryLabel={categoryLabel} />
          ) : (
            <div className="space-y-4">
              <section aria-label="Resumo">
                <p className="mb-2 font-poppins text-[10px] font-semibold uppercase tracking-[0.12em] text-sky-200/45">
                  Resumo
                </p>
                <PriceActivitySummary summary={summary} />
              </section>

              <section aria-label="Filtros" className="space-y-2">
                <p className="font-poppins text-[10px] font-semibold uppercase tracking-[0.12em] text-sky-200/45">
                  Alterações recentes
                </p>
                <PriceActivityFilters
                  direction={direction}
                  metric={metric}
                  onDirectionChange={(next) => {
                    setDirection(next);
                    setVisibleCount(PRICE_ACTIVITY_MODAL_PAGE_SIZE);
                  }}
                  onMetricChange={(next) => {
                    setMetric(next);
                    setVisibleCount(PRICE_ACTIVITY_MODAL_PAGE_SIZE);
                  }}
                />
              </section>

              {filtered.length === 0 ? (
                <p className="py-8 text-center font-inter text-sm text-sky-200/50">
                  Nenhuma alteração corresponde aos filtros.
                </p>
              ) : (
                <>
                  <PriceActivityList changes={visible} />
                  {hasMore ? (
                    <button
                      type="button"
                      onClick={() =>
                        setVisibleCount((n) => n + PRICE_ACTIVITY_MODAL_PAGE_SIZE)
                      }
                      className={cn(
                        'w-full rounded-xl border border-sky-400/30 bg-sky-500/10 px-4 py-2.5',
                        'font-poppins text-xs font-semibold text-sky-100',
                        'transition-colors hover:border-sky-300/45 hover:bg-sky-400/15',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50'
                      )}
                    >
                      Ver todas as alterações ({filtered.length - visibleCount} restantes)
                    </button>
                  ) : null}
                </>
              )}
            </div>
          )}
        </div>

        <div className="relative shrink-0 border-t border-white/[0.08] px-5 py-3.5 sm:px-6">
          <button
            type="button"
            onClick={() => {
              onOpenChange(false);
              onViewAllPowers();
            }}
            className={cn(
              'inline-flex w-full items-center justify-center gap-2 rounded-xl',
              'border border-sky-400/35 bg-sky-500/15 px-4 py-2.5',
              'font-poppins text-xs font-semibold text-sky-50',
              'transition-colors hover:border-sky-300/50 hover:bg-sky-400/20',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50'
            )}
          >
            Ver todos os powers
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
